import {
	WebPushService,
	type WebPushEnableResultKind,
	type WebPushTargetModel,
} from '@supply-mover-2-0/web-ui';
import {
	getPortalPushPublicKey,
	registerPortalPushSubscription,
	unregisterPortalPushSubscription,
} from '../services/portalNotificationService';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { isInsideNativeApp } from './mobileApp';

/**
 * PD-16475 — browser push for the storefront.
 *
 * Reuses the CRM's subscribe flow (WebPushService) with the storefront's own
 * service worker (`public/portal-sw.js`, push-only: it handles `push` and
 * `notificationclick` and does no fetch caching) and its own API. The worker
 * is registered only when the customer turns browser notifications on, never
 * on page load.
 */
export const PORTAL_WEB_PUSH_TARGET: WebPushTargetModel = {
	serviceWorkerPath: '/portal-sw.js',
	getPublicKey: async () =>
		(await getPortalPushPublicKey(usePortalAuthStore.getState().token))
			.publicKey,
	register: async (subscription) => {
		await registerPortalPushSubscription(
			usePortalAuthStore.getState().token,
			subscription,
		);
	},
	unregister: async (endpoint) => {
		await unregisterPortalPushSubscription(
			usePortalAuthStore.getState().token,
			endpoint,
		);
	},
};

/**
 * Whether to offer browser notifications at all. Not inside the storefront's
 * own iOS / Android app: a Capacitor web view has no browser service worker,
 * and app push is a separate, later feature (PD-14302).
 */
export function canOfferBrowserPush(): boolean {
	return !isInsideNativeApp() && WebPushService.isBrowserPushSupported();
}

/**
 * Ask permission and subscribe this browser, saying how it ended (PD-17322):
 * `push-unavailable` means notifications are allowed but this browser cannot
 * receive push (Brave by default), which the settings page answers with
 * open-tab notifications.
 */
export async function enablePortalBrowserPush(): Promise<WebPushEnableResultKind> {
	if (!canOfferBrowserPush()) {
		return 'unsupported';
	}
	return WebPushService.enableWithResult(PORTAL_WEB_PUSH_TARGET);
}

/**
 * PD-17322 — whether this tab may show open-tab notifications right now: a
 * browser (not the storefront app) whose notification permission is still
 * granted. Checked again before each notification, because the customer can
 * revoke it in the browser at any time.
 */
export function canShowTabNotifications(): boolean {
	return (
		!isInsideNativeApp() &&
		typeof Notification !== 'undefined' &&
		Notification.permission === 'granted'
	);
}

export async function disablePortalBrowserPush(): Promise<void> {
	if (!canOfferBrowserPush()) {
		return;
	}
	await WebPushService.disable(PORTAL_WEB_PUSH_TARGET);
}

/** Whether this browser currently holds a push subscription for the storefront. */
export async function isPortalBrowserPushOn(): Promise<boolean> {
	if (!canOfferBrowserPush()) {
		return false;
	}
	return WebPushService.isSubscribed();
}
